/**
 * VideoCallControls — the bottom control bar of the 1:1 chat video-call page.
 *
 * Web port of the Flutter `_bottomWidget` column (`video_call_screen.dart:1125-1343`),
 * a `Column` of `[_sendGiftWidget(), _streamActions()]` — the send-gift circle
 * raised above the action row. The action row (`_streamActions`, `:1224-1342`) is
 * a full-width `Row` of `[end-call, mute] · Expanded spacer · [camera,
 * switch-camera]`. On the web (sanctioned UI deviation, `presentation.md` "Web
 * vs Flutter UI parity scope") the bar is a centred floating card: it is lifted
 * off the viewport edges with bottom + side gaps (clearing the device safe-area
 * insets), all four corners are rounded, and all five controls — the send-gift
 * circle plus the four action icons — are horizontally centred in a single row
 * with a uniform gap (instead of the send-gift circle being raised above the row
 * and the actions being edge-spread by the Flutter `Expanded` spacer). Only
 * layout deviates — the business logic matches Flutter.
 *
 *   - send gift   → `gift.png` (`:1212`): a circular `AppColors.exclusive @ .5`
 *                   button, faded + tap-ignored until the audience joins
 *                   (`_audienceLiveStreamingUserId == null`, `:1156-1162`); on
 *                   tap it fires `onSendGift`, and the page opens the send-gift
 *                   sheet in its video-call variant (Flutter `:1163-1202` →
 *                   `NavigationService.showSendGiftSheet`).
 *   - end call    → `hang-up.png` (`:1253`): fires `onEndCall`; the page shows
 *                   the confirm sheet, then leaves + pops on confirm.
 *   - mute/unmute → `mic.png` (`:1270`): `muteLocalAudioStream`; a strike line
 *                   crosses the icon when muted (`:1275-1288`).
 *   - camera      → `video_cam.png` (`:1303`): `muteLocalVideoStream`; a strike
 *                   line crosses the icon when off (`:1309-1322`).
 *   - switch cam  → `switch-camera.png` (`:1333`): cycles the camera device.
 *                   Always rendered for layout symmetry, but disabled (faded,
 *                   no-op) when fewer than two cameras are present.
 *
 * The mute/camera actions and the underlying Agora calls live in the Task-10
 * `useVideoCall` hook (`toggleMute` / `toggleCamera` / `switchCamera` / `endCall`
 * + `isMute` / `isCameraOff`); this component is the presentational bar that maps
 * taps onto callbacks. Both the send-gift sheet and the end-call confirm flow are
 * owned by the page (`VideoCallPage`), not this bar: Flutter shows a `ConfirmSheet`
 * before ending (`:1233-1248`) and opens the send-gift sheet on tap (`:1163-1202`),
 * and on the web both live on the page (which holds the call context + shares the
 * confirm gate with the back arrow), so this component just fires `onSendGift` /
 * `onEndCall`.
 *
 * Visibility: the page only mounts this when
 * `!clearScreen && !callEnded && !isOpeningChannel` (Flutter `_bottomWidget`,
 * `:1126-1131`), so this component does not re-derive that gate.
 *
 * **Deviations from plan:** the Flutter strike is a `Transform.rotate(-pi/3)`
 * 1.5px line drawn over the icon (`:1275-1288`); the web renders the same effect
 * as an absolutely-positioned rotated `theme.white` line (-60deg) over the icon,
 * a sanctioned UI rendering of the same affordance. Icon color is `theme.white`
 * (always white over the dark video surface) mirroring Flutter `AppColors.white`
 * on every control, rather than `theme.icon.primary`, since the bar overlays
 * video in both themes. The gift icon uses `theme.whiteLight` mirroring Flutter
 * `AppColors.whiteLight` (`:1215`).
 *
 * Theme/responsive/l10n + `qaAttr` are mandatory (`presentation.md`); the five
 * control icons carry their localized name as the button `aria-label`.
 */

import React from 'react';

import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { AssetIcon } from '@/presentation/components/common/icon/AssetIcon';
import { qaAttr } from '@/presentation/utils/qaAttr';
import { ANIMATION_CONSTANTS } from '@/presentation/constants/animationConstants';

export interface VideoCallControlsProps {
  /** `_isMute` — drives the strike on the mic icon (`:1262-1275`). */
  isMute: boolean;
  /** `_isCameraOff` — drives the strike on the camera icon (`:1296-1309`). */
  isCameraOff: boolean;
  /** Whether a local camera track exists — switch/camera enabled only when so. */
  hasCameraTrack: boolean;
  /**
   * Number of available cameras, enumerated once by `useVideoCall` and forwarded
   * through the page. The switch-camera button is disabled when `< 2`. This
   * component no longer enumerates cameras itself.
   */
  cameraCount: number;
  /**
   * Whether the audience has joined the channel — the mirror of Flutter's
   * `_audienceLiveStreamingUserId != null` (`audienceStreamingId != null`, the
   * page derives this from `useVideoCall`). The send-gift button is faded
   * (opacity 0) and tap-ignored until this is `true` (Flutter `:1156-1162`).
   */
  audienceJoined: boolean;
  /**
   * Open the send-gift sheet in its video-call variant. The page owns the sheet
   * (it holds the call context), so this control bar just reports the tap.
   */
  onSendGift: () => void;
  /** Toggle mic mute (`useVideoCall.toggleMute`). */
  onToggleMute: () => void | Promise<void>;
  /** Toggle camera on/off (`useVideoCall.toggleCamera`). */
  onToggleCamera: () => void | Promise<void>;
  /** Cycle the camera device (`useVideoCall.switchCamera`). */
  onSwitchCamera: () => void | Promise<void>;
  /**
   * End-call tap. The confirm gate now lives on the page side (the page shows the
   * `ConfirmSheet` and calls `useVideoCall.endCall` on confirm, Flutter
   * `_onBackPress`), so this button reports the tap directly.
   */
  onEndCall: () => void;
}

export const VideoCallControls: React.FC<VideoCallControlsProps> = ({
  isMute,
  isCameraOff,
  hasCameraTrack,
  cameraCount,
  audienceJoined,
  onSendGift,
  onToggleMute,
  onToggleCamera,
  onSwitchCamera,
  onEndCall,
}) => {
  const { theme } = useTheme();
  const { l10n } = useLocale();
  const responsive = useResponsiveDesign();

  const copy = l10n.chat.videoCallPage;

  // The count now arrives as a prop from the hook's single enumeration
  // (Finding 10) instead of a local `getCameras()` call. The switch-camera
  // button is always rendered for layout symmetry but disabled when fewer than
  // two cameras are present (mirroring the camera button's disabled pattern).
  const canSwitchCamera = cameraCount >= 2;

  // Shared button chrome — a transparent, fixed-size touch target centring its
  // icon (the bar overlays video, so no surface fill; matches Flutter's bare
  // `GestureDetector` around each `Image.asset`).
  const buttonBaseStyle: React.CSSProperties = {
    position: 'relative',
    width: responsive.videoCall.controlHitSize,
    height: responsive.videoCall.controlHitSize,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
  };

  // The rotated strike line drawn over the mic/camera icon when muted/off
  // (Flutter `Transform.rotate(-pi/3)` 1.5px line, `:1262-1275`/`:1296-1309`).
  const strikeStyle: React.CSSProperties = {
    position: 'absolute',
    width: responsive.videoCall.controlStrikeWidth,
    height: responsive.videoCall.controlIconSize,
    backgroundColor: theme.white,
    transform: `rotate(${ANIMATION_CONSTANTS.rotation.strikeThrough}deg)`,
    pointerEvents: 'none',
  };

  // The send-gift circle: a fixed-size circular `exclusive @ .5` button (Flutter
  // `AppColors.exclusive.withValues(alpha: .5)`, `:1206`), via the
  // `exclusiveTransparent` theme token.
  const giftButtonStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: responsive.videoCall.giftButtonPadding,
    border: 'none',
    borderRadius: '50%',
    backgroundColor: theme.exclusiveTransparent,
    // Faded + tap-ignored until the audience joins (Flutter `:1156-1162`).
    opacity: audienceJoined ? 1 : 0,
    pointerEvents: audienceJoined ? 'auto' : 'none',
    cursor: 'pointer',
    transition: `opacity ${ANIMATION_CONSTANTS.duration.long}ms`,
  };

  return (
    <div
      {...qaAttr({ id: 'video-call-controls' })}
      style={{
        // Float the bar off the viewport edges (web deviation from Flutter's
        // edge-to-edge `_bottomWidget`): a bottom offset that still clears the
        // device bottom safe-area inset, plus symmetric left/right margins that
        // also clear the side insets, so the bar reads as a centred card.
        position: 'absolute',
        // left: `calc(${responsive.safeArea.left} + ${responsive.videoCall.controlBarFloatGap}px)`,
        // right: `calc(${responsive.safeArea.right} + ${responsive.videoCall.controlBarFloatGap}px)`,
        // center the bar horizontally (Flutter `_bottomWidget` is full-width, but the web deviation is a centred floating card)
        left: '50%',
        transform: 'translateX(-50%)',
        bottom: `calc(${responsive.safeArea.bottom} + ${responsive.videoCall.controlBarFloatGap}px)`,
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        // All controls sit inline in a single row (web deviation): the send-gift
        // circle is no longer raised above the action row, so it shares the
        // uniform `controlGap` with the four action icons (Flutter's `Column` of
        // `[gift, actions]`, `:1147`, is flattened here).
        gap: responsive.videoCall.controlGap,
        paddingTop: responsive.videoCall.controlBarPaddingY,
        paddingBottom: responsive.videoCall.controlBarPaddingY,
        paddingLeft: responsive.videoCall.controlBarPaddingX,
        paddingRight: responsive.videoCall.controlBarPaddingX,
        // All four corners rounded now that the bar floats (web deviation).
        borderRadius: responsive.videoCall.controlBarRadius,
        background: theme.whiteLightTransparent,
        zIndex: 6,
      }}
    >

      {/* Action row. Flutter `_streamActions` (`:1224-1342`) edge-spreads the
          four icons (`[end-call, mute] · Expanded spacer · [camera,
          switch-camera]`); on the web (deviation) they are horizontally centred
          in a single flex row with a uniform `controlGap`, now joined inline by
          the send-gift circle to its left. */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: responsive.videoCall.controlGap,
        }}
      >
        {/* End call (Flutter `:1232-1259`). */}
        <button
          {...qaAttr({ id: 'video-call-end' })}
          type="button"
          aria-label={copy.endCallLabel}
          onClick={onEndCall}
          style={buttonBaseStyle}
        >
          <AssetIcon
            path="/assets/images/hang-up.png"
            width={responsive.videoCall.controlIconSize}
            height={responsive.videoCall.controlIconSize}
            color={theme.white}
          />
        </button>

        {/* Mute / unmute (Flutter `:1260-1291`). Strike line when muted. */}
        <button
          {...qaAttr({ id: 'video-call-mute', value: isMute ? 'muted' : 'unmuted' })}
          type="button"
          aria-label={copy.muteLabel}
          aria-pressed={isMute}
          onClick={() => void onToggleMute()}
          style={buttonBaseStyle}
        >
          <AssetIcon
            path="/assets/images/mic.png"
            width={responsive.videoCall.controlIconSize}
            height={responsive.videoCall.controlIconSize}
            color={theme.white}
          />
          {isMute && <div style={strikeStyle} />}
        </button>

        <div style={{ width: responsive.videoCall.controlGap }} />

        {/* Send gift (Flutter `_sendGiftWidget`, `:1156-1222`). Inline in the
          control row (elevation removed), faded + tap-ignored until the audience
          joins. */}
        <button
          {...qaAttr({ id: 'video-call-gift' })}
          type="button"
          aria-label={copy.giftLabel}
          aria-hidden={!audienceJoined}
          tabIndex={audienceJoined ? 0 : -1}
          onClick={onSendGift}
          style={giftButtonStyle}
        >
          <AssetIcon
            path="/assets/images/gift.png"
            width={responsive.videoCall.giftIconSize}
            height={responsive.videoCall.giftIconSize}
            color={theme.whiteLight}
          />
        </button>

        <div style={{ width: responsive.videoCall.controlGap }} />

        {/* Camera on / off (Flutter `:1293-1325`). Strike line when off. Disabled
            until a local camera track exists. */}
        <button
          {...qaAttr({
            id: 'video-call-camera',
            value: isCameraOff ? 'off' : 'on',
          })}
          type="button"
          aria-label={copy.cameraLabel}
          aria-pressed={isCameraOff}
          disabled={!hasCameraTrack}
          onClick={() => void onToggleCamera()}
          style={{
            ...buttonBaseStyle,
            cursor: hasCameraTrack ? 'pointer' : 'default',
            opacity: hasCameraTrack ? 1 : 0.5,
          }}
        >
          <AssetIcon
            path="/assets/images/video_cam.png"
            width={responsive.videoCall.controlIconSize}
            height={responsive.videoCall.controlIconSize}
            color={theme.white}
          />
          {isCameraOff && <div style={strikeStyle} />}
        </button>

        {/* Switch camera (Flutter `:1326-1339`). Always rendered for layout
            symmetry; disabled (faded, no-op) when fewer than two cameras
            are present, mirroring the camera button's disabled pattern. */}
        <button
          {...qaAttr({ id: 'video-call-switch-camera', value: String(cameraCount) })}
          type="button"
          aria-label={copy.switchCameraLabel}
          disabled={!canSwitchCamera}
          onClick={() => {
            if (canSwitchCamera) {
              void onSwitchCamera();
            }
          }}
          style={{
            ...buttonBaseStyle,
            cursor: canSwitchCamera ? 'pointer' : 'default',
            opacity: canSwitchCamera ? 1 : 0.5,
          }}
        >
          <AssetIcon
            path="/assets/images/switch-camera.png"
            width={responsive.videoCall.controlIconSize}
            height={responsive.videoCall.controlIconSize}
            color={theme.white}
          />
        </button>
      </div>
    </div>
  );
};
